import Button from 'components/button'
import { Dialog, Slider, SizableText, View, XStack, YStack } from 'tamagui'
import Modal from 'components/modal'
import { useEffect, useRef, useState } from 'react'
import _AvatarEditor from 'react-avatar-editor'
import { Check, FileUploadRoundedIcon } from 'components/icons'

const AvatarEditor = _AvatarEditor as any

interface EditAvatarModalProps {
    image: string | null
    name?: string
    onClose: () => void
    onSave: (base64String: string) => void
}

const MIN_ZOOM = 1
const MAX_ZOOM = 3
const zoomStep = 0.05

export default ({ image, name, onClose, onSave }: EditAvatarModalProps) => {
    const editorRef = useRef<any>(null)
    const fileInputRef = useRef<HTMLInputElement>(null)

    const [imageSrc, setImageSrc] = useState<string | null>(image)
    const [zoomValue, setZoomValue] = useState(1)
    const [dragging, setDragging] = useState(false)

    // Resync internal state each time the modal is reopened with a fresh image.
    useEffect(() => {
        if (image) {
            setImageSrc(image)
            setZoomValue(1)
        }
    }, [image])

    const loadFile = (file?: File | null) => {
        if (!file || !file.type.startsWith('image/')) return
        setImageSrc(URL.createObjectURL(file))
        setZoomValue(1)
    }

    return (
        <Modal
            open={image != null}
            onOpenChange={open => {
                if (!open) onClose()
            }}
            width={440}
        >
            {/* Header */}
            <XStack
                justifyContent="space-between"
                alignItems="flex-start"
                gap="$3"
            >
                <YStack
                    flex={1}
                    minWidth={0}
                    gap="$1"
                >
                    <Dialog.Title
                        fontWeight="700"
                        letterSpacing={-0.3}
                        size="$6"
                    >
                        Update photo
                    </Dialog.Title>
                    {name ? (
                        <SizableText
                            size="$1"
                            color="$colorMuted"
                        >
                            {name}
                        </SizableText>
                    ) : null}
                </YStack>
            </XStack>

            {/* Circular preview / cropper */}
            <YStack
                alignItems="center"
                gap="$2"
            >
                <View
                    borderRadius={9999}
                    overflow="hidden"
                    borderWidth={1}
                    borderColor="$borderColor"
                    onWheel={(event: any) => {
                        const delta = event.deltaY < 0 ? zoomStep : -zoomStep
                        setZoomValue(value => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, value + delta)))
                    }}
                >
                    <AvatarEditor
                        ref={editorRef}
                        width={224}
                        height={224}
                        border={0}
                        borderRadius={112}
                        color={[0, 0, 0, 0.45]}
                        image={imageSrc ?? ''}
                        scale={zoomValue}
                        style={{ display: 'block', cursor: 'grab' }}
                    />
                </View>
                <SizableText
                    fontSize={11}
                    color="$colorMuted"
                >
                    Drag the photo to reposition
                </SizableText>
            </YStack>

            {/* Replace / browse drop zone */}
            <div
                onClick={() => fileInputRef.current?.click()}
                onDragOver={event => {
                    event.preventDefault()
                    setDragging(true)
                }}
                onDragLeave={() => setDragging(false)}
                onDrop={event => {
                    event.preventDefault()
                    setDragging(false)
                    loadFile(event.dataTransfer.files?.[0])
                }}
                style={{
                    border: `1.5px dashed ${dragging ? 'var(--primary)' : 'var(--borderColorStrong)'}`,
                    background: dragging ? 'var(--accentSoft)' : 'var(--backgroundRaised)',
                    borderRadius: 10,
                    padding: '18px 16px',
                    cursor: 'pointer',
                    transition: 'border-color 0.12s, background 0.12s',
                }}
            >
                <YStack
                    alignItems="center"
                    gap="$1.5"
                >
                    <FileUploadRoundedIcon
                        size={18}
                        color="$colorMuted"
                    />
                    <SizableText
                        size="$2"
                        fontWeight="500"
                    >
                        Replace photo
                    </SizableText>
                    <SizableText
                        fontSize={11}
                        color="$colorMuted"
                    >
                        JPG or PNG · max 2 MB · square works best
                    </SizableText>
                </YStack>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg"
                    style={{ display: 'none' }}
                    onChange={event => loadFile(event.target.files?.[0])}
                />
            </div>

            {/* Zoom */}
            <XStack
                alignItems="center"
                gap="$3"
            >
                <SizableText
                    size="$1"
                    color="$colorMuted"
                    width={36}
                >
                    Zoom
                </SizableText>
                <View flex={1}>
                    <Slider
                        value={[zoomValue]}
                        onValueChange={([value]) => setZoomValue(value)}
                        min={MIN_ZOOM}
                        max={MAX_ZOOM}
                        step={zoomStep}
                    >
                        <Slider.Track height={4}>
                            <Slider.TrackActive backgroundColor="$primary" />
                        </Slider.Track>
                        <Slider.Thumb
                            index={0}
                            circular
                            size={14}
                            borderWidth={0}
                            backgroundColor="$primary"
                        />
                    </Slider>
                </View>
                <SizableText
                    fontFamily="$mono"
                    color="$colorMuted"
                    textAlign="right"
                    width={42}
                    size="$1"
                >
                    {Math.round(zoomValue * 100)}%
                </SizableText>
            </XStack>

            {/* Footer */}
            <XStack
                gap="$2"
                justifyContent="flex-end"
            >
                <Button
                    onPress={onClose}
                    variant="outlined"
                    animation="quick"
                >
                    Cancel
                </Button>
                <Button
                    theme="primary"
                    animation="quick"
                    icon={<Check size={15} />}
                    onPress={async () => {
                        await onSave((editorRef.current as any).getImageScaledToCanvas().toDataURL())
                        onClose()
                    }}
                >
                    Save photo
                </Button>
            </XStack>
        </Modal>
    )
}
